import { useEffect, useState, type CSSProperties } from 'react';

// StagedLoading — Polish B2 reliability tranche. Generalizes ParsingIndicator's staged-loading pattern
// (spinner + timer-advanced messages that HOLD on the final stage) so every long LLM wait in the app uses
// ONE component instead of several inline copies. Honest copy only: never a fake determinate progress bar,
// never a claimed step that isn't real — the final stage is written to comfortably outlast the real worst-case
// wait, then holds indefinitely (never implies imminent completion past that point).

export function useStagedMessage(stages: string[], intervalMs: number, active = true): string {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (!active) { setStage(0); return; }
    setStage(0);
    const t = setInterval(() => setStage((s) => Math.min(s + 1, stages.length - 1)), intervalMs);
    return () => clearInterval(t);
  }, [stages, intervalMs, active]);
  return stages[Math.min(stage, stages.length - 1)] ?? '';
}

export function StagedLoading({
  stages, intervalMs = 4000, active = true, subtext = 'This takes a moment.', testId = 'staged-loading',
  stageTestId, variant = 'block',
}: {
  stages: string[];
  intervalMs?: number;
  active?: boolean;
  subtext?: string | null;
  testId?: string;
  stageTestId?: string;
  variant?: 'block' | 'inline';
}) {
  const message = useStagedMessage(stages, intervalMs, active);
  const stageId = stageTestId ?? `${testId}-stage`;
  if (variant === 'inline') {
    return (
      <span style={SI.inline} data-testid={testId}>
        <span style={SI.spinnerSmall} aria-hidden="true" />
        <span aria-live="polite" data-testid={stageId}>{message}</span>
      </span>
    );
  }
  return (
    <main style={S.main} data-testid={testId}>
      <style>{`@keyframes cvc-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={S.spinner} aria-hidden="true" />
      <p style={S.msg} aria-live="polite" data-testid={stageId}>{message}</p>
      {subtext && <p style={S.sub}>{subtext}</p>}
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 560, margin: '64px auto', padding: '0 var(--space-4)', textAlign: 'center', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  spinner: { display: 'inline-block', width: 22, height: 22, borderRadius: '50%', border: '2px solid var(--rule)', borderTopColor: 'var(--ink)', animation: 'cvc-spin 0.8s linear infinite' },
  msg: { marginTop: 'var(--space-4)', fontSize: 'var(--fs-body)', color: 'var(--ink)' },
  sub: { marginTop: 'var(--space-1)', fontSize: 'var(--fs-label)', color: 'var(--muted)' },
};
const SI: Record<string, CSSProperties> = {
  inline: { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--muted)' },
  spinnerSmall: { display: 'inline-block', width: 13, height: 13, borderRadius: '50%', border: '2px solid var(--rule)', borderTopColor: 'var(--ink)', animation: 'cvc-spin 0.8s linear infinite' },
};
